import { useTranslate } from './i18n.js';
/** The first `extensions.code` of a GraphQL error response, read off the shape. */
function graphQLCode(error) {
    const errors = error?.errors;
    const code = Array.isArray(errors) ? errors[0]?.extensions?.code : undefined;
    return typeof code === 'string' ? code : undefined;
}
function statusCode(error) {
    const status = error?.statusCode;
    return typeof status === 'number' ? status : undefined;
}
/**
 * A request that never got an answer. `fetch` rejects with a `TypeError`
 * whose message differs by browser — "Failed to fetch" in Chromium,
 * "NetworkError when attempting to fetch resource." in Firefox, "Load failed"
 * in Safari — and a request that was aborted for taking too long is an
 * `AbortError`. All of them mean the same thing to the person waiting.
 */
function isUnanswered(error) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false)
        return true;
    if (!(error instanceof Error))
        return false;
    if (error.name === 'AbortError' || error.name === 'TimeoutError')
        return true;
    return (error instanceof TypeError ||
        /failed to fetch|networkerror|load failed|network request failed/i.test(error.message));
}
/**
 * The kind of failure an error is, read from its shape rather than its class.
 *
 * Shape, not `instanceof`, because the classes belong to Apollo, this package
 * does not depend on Apollo, and an `instanceof` across two copies of a
 * package answers false anyway (`apps/web/src/lib/errors.ts` says the same).
 * The codes are the ones the API attaches (V1.md section 5).
 */
export function failureKind(error) {
    switch (graphQLCode(error)) {
        case undefined:
            break;
        case 'UNAUTHENTICATED':
            return 'signedOut';
        case 'FORBIDDEN':
            return 'forbidden';
        case 'NOT_FOUND':
            return 'notFound';
        case 'BAD_USER_INPUT':
        case 'GRAPHQL_VALIDATION_FAILED':
        case 'GRAPHQL_PARSE_FAILED':
            return 'invalid';
        default:
            return 'unexpected';
    }
    const status = statusCode(error);
    if (status !== undefined) {
        if (status === 401)
            return 'signedOut';
        if (status === 403)
            return 'forbidden';
        if (status === 404)
            return 'notFound';
        // A gateway that could not reach the API, or an API not up yet (a cold
        // start behind the proxy): the same thing to the reader as no connection.
        if (status === 502 || status === 503 || status === 504)
            return 'connection';
        return 'unexpected';
    }
    return isUnanswered(error) ? 'connection' : 'unexpected';
}
/** Whether doing the same thing again could work. */
export function isRetryable(kind) {
    return kind === 'connection' || kind === 'unexpected';
}
/**
 * `(error) => sentence`, saying why an action did not complete and what to do
 * about it, in the reader's language. Plain product language: never the
 * status code, the error class or the server's own text.
 */
export function useFailureReason() {
    const t = useTranslate();
    return (error) => t(`ui.failure.${failureKind(error)}`);
}
//# sourceMappingURL=failure.js.map